

import React, { Component } from 'react';
import anime from 'animejs';
import cx from 'classnames';
import DragHandler from '../../DragHandler';
import '../styles/_seek-bar.scss';
















const SKIP_AMOUNT = 1;
const KEY_CODES = { left: 37, right: 39 };
const SEEK_BAR_CLASS = 'seek-bar';
const SEEK_BAR_FILL_CLASS = 'seek-bar__fill';
const SEEK_BAR_HANDLE_CLASS = 'seek-bar__handle';

function hasSeekTimeChanged(isPlaying, oldTime, newTime) {
  if (!isPlaying) {
    return oldTime !== newTime;
  }
  return Math.abs(oldTime - newTime) >= SKIP_AMOUNT;
}

function attemptToUpdateSeekBarWidth(
valuePercent,
seekBarFill,
seekBarHandle)
{
  if (!seekBarFill.current || !seekBarHandle.current) {return;}

  const seekBarFillStyle = seekBarFill.current.style;
  seekBarFillStyle.width = `${valuePercent}%`;

  const seekBarHandleStyle = seekBarHandle.current.style;
  seekBarHandleStyle.left = `calc(${valuePercent}% - 5px)`;
}

function calculateLeftSeconds(
{ totalDuration, currentTime })
{
  return totalDuration - currentTime;
}

export default class SeekBar extends Component {










  constructor(props) {
    super(props);
    this._barRef = React.createRef();
    this._fillRef = React.createRef();
    this._handleRef = React.createRef();

    this.state = {
      lostFocus: false
    };
  }

  componentDidMount() {
    window.addEventListener('focus', this.onFocus);
    window.addEventListener('blur', this.onBlur);

    this.syncSeekBarWidth();
  }

  shouldComponentUpdate(nextProps, nextState) {
    const { currentTime, isPlaying, totalDuration, commentModalId, speed } = this.props;

    const { lostFocus: hasLostFocus } = this.state;

    const hasRegainedFocus = hasLostFocus && !nextState.lostFocus;

    return (
      isPlaying !== nextProps.isPlaying ||
      hasRegainedFocus ||
      hasSeekTimeChanged(isPlaying, currentTime, nextProps.currentTime) ||
      totalDuration !== nextProps.totalDuration ||
      Boolean(!nextProps.currentTime && currentTime) ||
      Boolean(nextProps.commentModalId && nextProps.commentModalId !== commentModalId) ||
      speed !== nextProps.speed);

  }

  componentDidUpdate(
  { currentTime: prevCurrentTime, totalDuration: prevTotalDuration },
  { lostFocus: prevLostFocus })
  {
    const { currentTime: newCurrentTime, totalDuration, isPlaying } = this.props;
    const { lostFocus: hasLostFocus } = this.state;
    const videoRestarted = !newCurrentTime && prevCurrentTime;

    if (videoRestarted) {
      attemptToUpdateSeekBarWidth(0, this._fillRef, this._handleRef);
    }


    if (
    hasSeekTimeChanged(isPlaying, prevCurrentTime, newCurrentTime) ||
    prevTotalDuration !== totalDuration ||
    prevLostFocus !== hasLostFocus)
    {
      this.syncSeekBarWidth();
    }

    this.setAnimation();
  }

  componentWillUnmount() {
    window.removeEventListener('focus', this.onFocus);
    window.removeEventListener('blur', this.onBlur);
  }

  // The fill and handle are positioned imperatively, so they are only ever as
  // correct as the last time we wrote to them. Redraw from the current props
  // whenever the position or the duration it is a fraction of has moved.
  syncSeekBarWidth = () => {
    const { currentTime, totalDuration } = this.props;

    if (!totalDuration) {
      return;
    }

    attemptToUpdateSeekBarWidth(
      currentTime / totalDuration * 100,
      this._fillRef,
      this._handleRef
    );
  };

  onFocus = () => {
    this.setState({ lostFocus: false });
  };

  onBlur = () => {
    this.setState({ lostFocus: true });
  };

  setAnimation(overrideTime) {
    const { isPlaying, isBuffering, speed } = this.props;

    if (!isPlaying || isBuffering) {
      return void this.pauseAnimation();
    }

    const { currentTime, totalDuration } = this.props;
    const leftSeconds = calculateLeftSeconds(
      { currentTime: overrideTime || currentTime, totalDuration }
    );

    if (this._animation) {this.pauseAnimation();}

    const animationProps = {
      duration: leftSeconds * 1000 / speed,
      easing: 'linear',
      autoplay: true,
      loop: false
    };

    this._animation = anime({
      targets: this._fillRef.current,
      width: '100%',
      ...animationProps
    });

    this._markerAnimation = anime({
      targets: this._handleRef.current,
      left: 'calc(100% - 5px)',
      ...animationProps
    });
  }

  handleSeekChange = ({ x }) => {
    const { totalDuration, onSetManualTime } = this.props;
    if (!this._barRef) {return;}

    const seekBar = this._barRef.current;
    if (!seekBar) {return;}

    const { offsetWidth } = seekBar;
    const { left } = seekBar.getBoundingClientRect();
    const adjustedX = x - left;
    const finalX = adjustedX > offsetWidth ? offsetWidth : adjustedX;

    if (finalX <= 0) {return void onSetManualTime(0);}

    const percentDecimal = finalX / offsetWidth;
    const newTime = totalDuration * percentDecimal;

    attemptToUpdateSeekBarWidth(percentDecimal * 100, this._fillRef, this._handleRef);

    this.setAnimation(newTime);

    onSetManualTime(newTime);
  };

  setFocus = () => {
    document.addEventListener('keydown', this.handleManualSeekMove);
  };

  setBlur = () => {
    document.removeEventListener('keydown', this.handleManualSeekMove);
  };

  handleManualSeekMove = ({ keyCode }) => {
    const { totalDuration, onSetManualTime, currentTime } = this.props;

    if (keyCode !== KEY_CODES.left && keyCode !== KEY_CODES.right) {return;}

    const newTime = keyCode === KEY_CODES.left ? currentTime - 5 : currentTime + 5;
    const normalisedMinTime = newTime < 0 ? 0 : newTime;
    const normalisedMaxTime = normalisedMinTime > totalDuration ? totalDuration : normalisedMinTime;
    const percentDecimal = normalisedMaxTime / totalDuration * 100;

    attemptToUpdateSeekBarWidth(percentDecimal, this._fillRef, this._handleRef);

    this.setAnimation(normalisedMaxTime);

    onSetManualTime(normalisedMaxTime);
  };

  pauseAnimation() {
    if (!this._animation || !this._markerAnimation) {return;}

    this._animation.pause();
    this._markerAnimation.pause();
  }

  render() {
    const { loading } = this.props;

    return (
      <DragHandler onDrag={this.handleSeekChange} cursor="grab">
        {({ onMouseDown, isDragging }) =>
        <div
          className={cx(
            SEEK_BAR_CLASS,
            { [`${SEEK_BAR_CLASS}--dragging`]: isDragging },
            { [`${SEEK_BAR_CLASS}--loading`]: loading }
          )}
          onMouseDown={onMouseDown}
          role="button"
          tabIndex={0}
          onFocus={this.setFocus}
          onBlur={this.setBlur}
          ref={this._barRef}>

            <div className={SEEK_BAR_HANDLE_CLASS} ref={this._handleRef} />
            <div
            className={cx(
              SEEK_BAR_FILL_CLASS,
              { [`${SEEK_BAR_FILL_CLASS}--dragging`]: isDragging }
            )}
            ref={this._fillRef} />

          </div>}

      </DragHandler>);

  }
}